/// <amd-module name="Wit/WorkItemModel/WorkItem/LegacyWorkItem" />
import { IVssPageContext } from "VSS/Platform/Context";
import { CoreField } from "Wit/Common/Generated/Constants";
import { ILinkInfo, IWorkItemLinkTypeEnd } from "Wit/WorkItemDataManager/Contracts";
import { ILegacyWorkItem, ILegacyWorkItemContract } from "Wit/WorkItemModel/OM/ILegacyWorkItem";
import { IField, IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { LegacyField } from "./LegacyField";
import { LegacyWorkItemStore } from "./LegacyStore";
import { Link } from "./Links";

/**
 * This is used for work item form replat to be compatible with controls that live under Tfs\Service\WebAccess\*, that expect a different WorkItem interface
 */
export class LegacyWorkItem implements ILegacyWorkItem {
    private _pageContext: IVssPageContext;
    private _workItem: IWorkItem;
    private _linkTypeEnds: IWorkItemLinkTypeEnd[];

    public id: number;
    public workItemType: {
        name: string;
    };
    public project: {
        guid: string;
        name: string;
        extras: any;
    };
    public store: LegacyWorkItemStore;
    public rev: number;
    public sessionId: string = "NewWorkItemFormLegacyContainer";
    public getAttachmentUri: ((link: ILinkInfo) => Promise<string>) | undefined;

    constructor(pageContext: IVssPageContext, workItem: IWorkItem, linkTypeEnds: IWorkItemLinkTypeEnd[]) {
        this._pageContext = pageContext;
        this._workItem = workItem;
        this._linkTypeEnds = linkTypeEnds;

        this.rev = workItem.revision.value;
        this.id = workItem.id;
        this.workItemType = {
            name: this._workItem.getFieldValueById(CoreField.WorkItemType) as string
        };
        this.project = {
            guid: this._workItem.project.guid,
            name: this._workItem.project.name,
            extras: this._workItem.project.extras
        };
        this.store = new LegacyWorkItemStore(pageContext, linkTypeEnds);
    }

    public getField(fieldReferenceOrId: number | string): LegacyField | undefined {
        if (typeof fieldReferenceOrId === "number") {
            const field: IField | undefined = this._workItem.getFieldById(fieldReferenceOrId);
            if (field === undefined) {
                return undefined;
            }

            return new LegacyField(this._pageContext, field, this._workItem.getFieldValueById(fieldReferenceOrId));
        } else {
            const field: IField | undefined = this._workItem.getFieldByName(fieldReferenceOrId);
            if (field === undefined) {
                return undefined;
            }

            return new LegacyField(this._pageContext, field, this._workItem.getFieldValueByName(fieldReferenceOrId));
        }
    }

    public getState(): string {
        return this.getFieldValue(CoreField.State) || "";
    }

    public getTitle(): string {
        return this.getFieldValue(CoreField.Title) || "";
    }

    public isReadOnly(): boolean {
        return this._workItem.isReadOnly();
    }

    public isCommentingAvailable(): boolean {
        return this._workItem.isCommentingAvailable();
    }

    public isNew(): boolean {
        return this._workItem.isNew();
    }

    public isSaving(): boolean {
        return this._workItem.saving.value;
    }

    public getFieldValue(fieldNameOrId: number | string, original?: boolean, omitSetByRule?: boolean): any {
        return typeof fieldNameOrId === "number"
            ? this._workItem.getFieldValueById(fieldNameOrId)
            : this._workItem.getFieldValueByName(fieldNameOrId);
    }

    public setFieldValue(field: string, value: string) {
        this._workItem.setFieldValueByName(field, value);
    }

    public attachFieldChange(fieldReferenceOrId: number | string, handler: any): void {}
    public detachFieldChange(fieldReferenceOrId: string | number, handler: any): void {}

    public attachWorkItemChanged(handler: any) {}
    public detachWorkItemChanged(handler: any) {}

    public getComputedFieldValue(fieldId: number, revisionIndex?: number): { success: boolean; value: any } | null {
        return this._workItem.getComputedFieldValue(fieldId);
    }

    public getWorkItemContract(): ILegacyWorkItemContract | null {
        if (this.isNew()) {
            return null;
        }

        const fieldValues: { [fieldRefName: string]: unknown } = {};
        this._workItem.fields.forEach(field => {
            fieldValues[field.fieldDefinition.referenceName] = this.getFieldValue(field.fieldDefinition.referenceName);
        });

        return {
            id: this.id,
            rev: this._workItem.revision.value,
            relations: null,
            fields: fieldValues,
            _links: null,
            url: this.getResourceUrl()
        };
    }

    public getResourceUrl(): string {
        // Technically unsafe, but practically safe. This will be '/orgname/` or '/' depending on whether they use a legacy url
        const rootPath = (this._pageContext.getRestClient("IWorkItemTrackingRestClient") as any)._options.rootPath;
        return `${window.location.origin}${rootPath}_apis/wit/workItems/${this.id}`;
    }

    public getLinks(): any[] {
        return this._workItem.getLinks().map(l => {
            const thisValue = {
                linkData: l.linkData,
                workItem: {
                    id: this._workItem.id
                },
                getAddedDate: (): Date | undefined => {
                    return l.getAddedDate();
                },
                getComment: (): string | undefined => {
                    return l.getComment();
                },
                getFilePath: (): string | undefined => {
                    return l.linkData.FilePath;
                },
                getFieldId: (): number => {
                    return l.getFieldId();
                },
                getLength: (): number | undefined => {
                    return l.linkData.Length;
                },
                getName: (): string | undefined => {
                    return l.linkData.OriginalName;
                },
                getUri: (_: boolean): string => {
                    return "";
                },
                remove: () => {
                    this._workItem.removeLinks([l]);
                },
                getTargetId: (): number => {
                    return l.linkData.ID!;
                },
                getLinkTypeEnd: (): any => {
                    for (let i = 0; i < this._linkTypeEnds.length; i++) {
                        const lte: IWorkItemLinkTypeEnd = this._linkTypeEnds[i];
                        if (l.linkData.LinkType! === lte.linkType!.reverseEnd.id) {
                            return {
                                immutableName: lte.linkType?.reverseEnd.immutableName
                            };
                        }
                    }
                },
                open: () => {
                    this.getAttachmentUri?.(l.linkData).then(uri => {
                        const fileName = l.linkData.OriginalName;
                        const attachmentUri = uri.concat(fileName ? `?fileName=${fileName}` : "");
                        window.open(attachmentUri, "_blank");
                    });
                }
            };
            Object.defineProperty(thisValue, "comment", {
                get: function () {
                    return l.comment;
                },
                set: function (val) {
                    l.setComment(val);
                }
            });
            return thisValue;
        });
    }

    public addLink(link: any) {
        this._workItem.addLinks([this.getReplatLink(link)]);
    }

    public removeLinks(links: any[]) {
        const replatLinks: Link[] = links.map(l => {
            return this.getReplatLink(l);
        });
        this._workItem.removeLinks(replatLinks);
    }

    private getReplatLink(link: any): Link {
        const linkInfo: ILinkInfo = {
            ID: link.linkData.ID,
            Comment: link.comment,
            FilePath: link.linkData.FilePath,
            FldID: link.linkData.FldID,
            OriginalName: link.linkData.OriginalName,
            Length: link.linkData.Length,
            LastWriteDate: link.linkData.LastWriteDate,
            ExtID: link.linkData.ExtID,
            CreationDate: link.linkData.CreationDate,
            RemovedDate: link.linkData.RemovedDate,
            AddedDate: link.linkData.AddedDate,
            LinkType: link.linkData.LinkType
        };
        return Link.createFromLinkData(linkInfo);
    }
}
